// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@shared/data-access/config/citec-app';
import { selectClientInstitutions } from '@shared/data-access/slices/clients/clients-selectors';
import {
  buildLinkableProductsByIban,
  resolveLinkedProducts,
} from '../../utils/illiquid-linked-products';

export interface UseIlliquidAssetLinkedAccountsSectionProps {
  readonly draftIbans: readonly string[];

  readonly onLink: (iban: string) => void;

  readonly onUnlink: (iban: string) => void;
}

export const useIlliquidAssetLinkedAccountsSection = ({
  draftIbans,
  onLink,
  onUnlink,
}: UseIlliquidAssetLinkedAccountsSectionProps) => {
  const { t } = useTranslation();

  const institutions = useAppSelector(selectClientInstitutions);

  const byIban = useMemo(
    () => buildLinkableProductsByIban(institutions),
    [institutions],
  );

  const linkedProducts = useMemo(
    () => resolveLinkedProducts(draftIbans, byIban),
    [draftIbans, byIban],
  );

  const availableProducts = useMemo(
    () =>
      Array.from(byIban.values()).filter(
        product => !draftIbans.includes(product.iban),
      ),
    [byIban, draftIbans],
  );

  return {
    t,
    linkedProducts,
    availableProducts,
    onLink,
    onUnlink,
  } as const;
};
